import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API_POST } from "../General/Utility";
import { Show_Message } from "../General/Messagedialogbox";

// en-IN => lakh/crore grouping
const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const fmtINR = (v) => INR.format(Number(v) || 0);

/*
  Props expected from the parent (Pointofsales.js):
    Close()                    - close popup
    ViewInvoice(vouchId)       - open invoice in the POS screen
    Return(vouchId)            - open the Return flow
    PrintVoucher(vouchId, "Print" | "PDF")
    ShareVoucher(vouchId, "WhatsApp" | "Email")
*/
function PosView(props) {
  const dispatch = useDispatch();
  const _AuthParems = useSelector((state) => state.Common.Admin);
  const _Common = useSelector((store) => store.Transstates.Common);

  const [strSearch, setstrSearch] = useState("");
  const [strFromDate, setstrFromDate] = useState("");
  const [strToDate, setstrToDate] = useState("");
  const [VouchersList, setVouchersList] = useState([]);
  const [iVouchId, setiVouchId] = useState(0);
  const [isLoading, setisLoading] = useState(false);
  const [strBusyAction, setstrBusyAction] = useState(""); // blocks double clicks

  const itemRefs = useRef([]);
  const reqSeq = useRef(0);
  const searchTimer = useRef(null);
  const closeRef = useRef(props.Close);
  closeRef.current = props.Close;
  const printSettRef = useRef(false);
  printSettRef.current = !!_Common.IsPrintSett;

  useEffect(() => {
    fetchVouchers("", "", "");
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (printSettRef.current) return; // Print Settings popup is open on top
        e.preventDefault();
        if (typeof closeRef.current === "function") closeRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVouchers = async (Search, FromDate, ToDate) => {
    const seq = ++reqSeq.current;
    try {
      setisLoading(true);
      const res = await API_POST(
        `POS/load_Vouchers/`,
        {
          iBranchId: Number(_AuthParems.Branch),
          strFromDate: FromDate,
          strToDate: ToDate,
          strSearch: Search,
          iScrId: 93,
          strDBName: _AuthParems.DB,
          iCounterId: Number(_Common.iCounterId) || Number(_AuthParems.Counter) || 0,
        },
        dispatch
      );
      if (seq !== reqSeq.current) return;
      if (res) {
        setstrFromDate(res.strFromDate);
        setstrToDate(res.strToDate);
        setVouchersList(res.objVouchers || []);
        setiVouchId(0);
      }
    } catch (err) {
      if (seq === reqSeq.current) Show_Message(dispatch, err.message, "error");
    } finally {
      if (seq === reqSeq.current) setisLoading(false);
    }
  };

  const Search_Change = (value) => {
    setstrSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchTimer.current = null;
      fetchVouchers(value, strFromDate, strToDate);
    }, 300);
  };
  const Date_Change = (isFrom, value) => {
    const from = isFrom ? value : strFromDate;
    const to = isFrom ? strToDate : value;
    isFrom ? setstrFromDate(value) : setstrToDate(value);
    if (from && to && from > to) {
      Show_Message(dispatch, "From Date cannot be after To Date", "info");
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = null;
    fetchVouchers(strSearch, from, to);
  };

  const focusItem = (i) => {
    const el = itemRefs.current[i];
    if (el) el.focus();
  };

  // ONE common runner for all 5 action buttons:
  //  1. invoice must be selected
  //  2. parent handler must exist (no more silent "nothing happens")
  //  3. handler errors are shown, double click is blocked
  const Run_Action = async (e, actionName, handlerName, ...extraArgs) => {
    if (e && e.preventDefault) e.preventDefault();
    if (strBusyAction) return;
    if (!Number(iVouchId)) {
      Show_Message(dispatch, "Please select an invoice", "info");
      return;
    }
    const handler = props[handlerName];
    // trace: shows in F12 Console exactly what the click carried
    console.debug("[PosView] click", actionName, {
      iVouchId,
      handler: handlerName,
      handlerIsFunction: typeof handler === "function",
    });
    if (typeof handler !== "function") {
      console.error(
        `[PosView] props.${handlerName} is not passed by the parent (Pointofsales.js). "${actionName}" cannot run.`
      );
      Show_Message(dispatch, `${actionName} is not available right now`, "error");
      return;
    }
    try {
      setstrBusyAction(actionName);
      await handler(iVouchId, ...extraArgs);
    } catch (err) {
      console.error(`[PosView] ${actionName} failed`, err);
      Show_Message(dispatch, err?.message || `${actionName} failed`, "error");
    } finally {
      setstrBusyAction("");
    }
  };

  const renderAction = ({ icon, label, onRun }) => (
    <div
      key={label}
      className="pos-tooltip"
      role="button"
      tabIndex={0}
      aria-label={label}
      style={{
        opacity: Number(iVouchId) && !strBusyAction ? 1 : 0.55,
        cursor: strBusyAction ? "wait" : "pointer",
      }}
      onClick={onRun}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onRun(e);
      }}
    >
      <a className="icon-btn" href="##" tabIndex={-1}>
        <i className={icon}></i>
      </a>
      <span className="pos-tooltiptxt">{label}</span>
    </div>
  );

  return (
    <div className="popup-container">
      <div className="popup-content">
        <div className="popup-header">
          <h5>Invoice</h5>
          <div className="icon-popup-btn-grp">
            {typeof props.PrintSettings === "function" && (
              <span
                className="icon-btn"
                title="Print Settings"
                onClick={props.PrintSettings}
              >
                <i className="fa-solid fa-gear"></i>
              </span>
            )}
            <span className="icon-btn" onClick={props.Close}>
              <i className="fa-regular fa-rectangle-xmark"></i>
            </span>
          </div>
        </div>
        <div className="popup-body">
          <div className="input-search">
            <input
              type="text"
              value={strSearch}
              onChange={(e) => Search_Change(e.target.value)}
              className="form-control"
              placeholder="Search bill number ...."
              autoFocus
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                  focusItem(0);
                } else if (e.key === "Enter") {
                  if (searchTimer.current) {
                    clearTimeout(searchTimer.current);
                    searchTimer.current = null;
                    fetchVouchers(strSearch, strFromDate, strToDate);
                  } else if (VouchersList.length > 0 && props.ViewInvoice) {
                    props.ViewInvoice(VouchersList[0].vouchId);
                  }
                }
              }}
            />
            <input
              type="date"
              value={strFromDate}
              onChange={(e) => Date_Change(true, e.target.value)}
              className="form-control"
              autoComplete="off"
            />
            <input
              type="date"
              value={strToDate}
              onChange={(e) => Date_Change(false, e.target.value)}
              className="form-control"
              autoComplete="off"
            />
          </div>
          <div className="pos-view">
            {!isLoading && VouchersList.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                No invoices found
              </div>
            )}
            {VouchersList.map((Vouch, trIndex) => (
              <button
                type="button"
                key={Vouch.vouchId}
                ref={(el) => (itemRefs.current[trIndex] = el)}
                className={iVouchId === Vouch.vouchId ? "active" : ""}
                onClick={() => setiVouchId(Vouch.vouchId)}
                onFocus={() => setiVouchId(Vouch.vouchId)}
                onDoubleClick={() => props.ViewInvoice && props.ViewInvoice(Vouch.vouchId)}
                onKeyDown={(e) => {
                  const last = VouchersList.length - 1;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    focusItem(trIndex === last ? 0 : trIndex + 1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    focusItem(trIndex === 0 ? last : trIndex - 1);
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    props.ViewInvoice && props.ViewInvoice(Vouch.vouchId);
                  }
                }}
              >
                <div className="bill-content">
                  <h6>{Vouch.partyName}</h6>
                  <div className="trans-cont">
                    <div className="transview1">
                      <label>
                        <span>Inv No : {Vouch.vouchNo}</span>
                        <span>Date : {Vouch.vouchDate}</span>
                        <span>Qty : {Number(Vouch.tQty).toFixed(2)}</span>
                        <span>Tax Amount : {fmtINR(Vouch.tTaxAmt)}</span>
                        <span>Net Amount : {fmtINR(Vouch.netAmt)}</span>
                      </label>
                      <label></label>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="pos-btn-group">
          {renderAction({
            icon: "fa-solid fa-print",
            label: "Print",
            onRun: (e) => Run_Action(e, "Print", "PrintVoucher", "Print"),
          })}
          {renderAction({
            icon: "fa-solid fa-download",
            label: "Download",
            onRun: (e) => Run_Action(e, "Download", "PrintVoucher", "PDF"),
          })}
          {renderAction({
            icon: "fa-solid fa-arrow-rotate-left",
            label: "Return",
            onRun: (e) => Run_Action(e, "Return", "Return"),
          })}
          {renderAction({
            icon: "fa-brands fa-whatsapp",
            label: "whatsapp",
            onRun: (e) => Run_Action(e, "WhatsApp", "ShareVoucher", "WhatsApp"),
          })}
          {renderAction({
            icon: "fa-regular fa-envelope",
            label: "E-mail",
            onRun: (e) => Run_Action(e, "E-mail", "ShareVoucher", "Email"),
          })}
        </div>
      </div>
    </div>
  );
}

export default PosView;



// import React, { useEffect, useRef, useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { API_POST } from "../General/Utility";
// import { Show_Message } from "../General/Messagedialogbox";

// // en-IN => lakh/crore grouping. (en-GB printed 1,234,567.00 for INR)
// const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
// const fmtINR = (v) => INR.format(Number(v) || 0);

// function PosView(props) {
//   const dispatch = useDispatch();
//   const _AuthParems = useSelector((state) => state.Common.Admin);
//   const _Common = useSelector((store) => store.Transstates.Common);

//   const [strSearch, setstrSearch] = useState("");
//   const [strFromDate, setstrFromDate] = useState("");
//   const [strToDate, setstrToDate] = useState("");
//   const [VouchersList, setVouchersList] = useState([]);
//   const [iVouchId, setiVouchId] = useState(0);
//   const [isLoading, setisLoading] = useState(false);

//   const itemRefs = useRef([]);
//   const reqSeq = useRef(0); // only the latest response may update the list
//   const searchTimer = useRef(null);
//   const closeRef = useRef(props.Close);
//   closeRef.current = props.Close;

//   useEffect(() => {
//     fetchVouchers("", "", "");
//     const onKey = (e) => {
//       if (e.key === "Escape") {
//         e.preventDefault();
//         closeRef.current();
//       }
//     };
//     document.addEventListener("keydown", onKey);
//     return () => {
//       document.removeEventListener("keydown", onKey);
//       clearTimeout(searchTimer.current);
//     };
//   }, []);

//   // ✅ FIX: no try/catch (API error = unhandled rejection, blank list) and no
//   // protection against out-of-order responses while typing fast.
//   const fetchVouchers = async (Search, FromDate, ToDate) => {
//     const seq = ++reqSeq.current;
//     try {
//       setisLoading(true);
//       const res = await API_POST(
//         `POS/load_Vouchers/`,
//         {
//           iBranchId: Number(_AuthParems.Branch),
//           strFromDate: FromDate,
//           strToDate: ToDate,
//           strSearch: Search,
//           iScrId: 93,
//           strDBName: _AuthParems.DB,
//           // ✅ FIX: localStorage.Counter does not exist (counter lives in the
//           // encrypted jbctrlparams / redux) -> NaN -> JSON null -> HTTP 400
//           // "$.iCounterId could not be converted to Int32".
//           iCounterId: Number(_Common.iCounterId) || Number(_AuthParems.Counter) || 0,
//         },
//         dispatch
//       );
//       if (seq !== reqSeq.current) return; // a newer request is in flight
//       if (res) {
//         setstrFromDate(res.strFromDate);
//         setstrToDate(res.strToDate);
//         setVouchersList(res.objVouchers || []);
//         setiVouchId(0);
//       }
//     } catch (err) {
//       if (seq === reqSeq.current) Show_Message(dispatch, err.message, "error");
//     } finally {
//       if (seq === reqSeq.current) setisLoading(false);
//     }
//   };

//   const Search_Change = (value) => {
//     setstrSearch(value);
//     clearTimeout(searchTimer.current);
//     // debounce: one API call per pause, not one per keystroke
//     searchTimer.current = setTimeout(() => {
//       searchTimer.current = null;
//       fetchVouchers(value, strFromDate, strToDate);
//     }, 300);
//   };
//   const Date_Change = (isFrom, value) => {
//     const from = isFrom ? value : strFromDate;
//     const to = isFrom ? strToDate : value;
//     isFrom ? setstrFromDate(value) : setstrToDate(value);
//     if (from && to && from > to) {
//       Show_Message(dispatch, "From Date cannot be after To Date", "info");
//       return;
//     }
//     clearTimeout(searchTimer.current);
//     searchTimer.current = null;
//     fetchVouchers(strSearch, from, to);
//   };

//   const focusItem = (i) => {
//     const el = itemRefs.current[i];
//     if (el) el.focus();
//   };
//   const Return_Click = () => {
//     // ✅ FIX: Return with nothing selected opened ProductReturn with VouchId 0
//     if (iVouchId === 0) {
//       Show_Message(dispatch, "Please select an invoice to return", "info");
//       return;
//     }
//     props.Return(iVouchId);
//   };
//   // ✅ FIX: Print / Download / WhatsApp / E-mail were <a href="##"> that did
//   // nothing (and changed the URL hash). Give feedback until they are wired up.
//   const Not_Ready = (e, name) => {
//     e.preventDefault();
//     Show_Message(dispatch, `${name} is not available yet`, "info");
//   };

//   return (
//     <div className="popup-container">
//       <div className="popup-content">
//         <div className="popup-header">
//           <h5>Invoice</h5>
//           <div className="icon-popup-btn-grp">
//             <span className="icon-btn" onClick={props.Close}>
//               <i className="fa-regular fa-rectangle-xmark"></i>
//             </span>
//           </div>
//         </div>
//         <div className="popup-body">
//           <div className="input-search">
//             <input
//               type="text"
//               value={strSearch}
//               onChange={(e) => Search_Change(e.target.value)}
//               className="form-control"
//               placeholder="Search bill number ...."
//               autoFocus
//               autoComplete="off"
//               onKeyDown={(e) => {
//                 if (e.key === "ArrowUp" || e.key === "ArrowDown") {
//                   e.preventDefault();
//                   focusItem(0);
//                 } else if (e.key === "Enter") {
//                   if (searchTimer.current) {
//                     // search still pending: run it now instead of opening a stale row
//                     clearTimeout(searchTimer.current);
//                     searchTimer.current = null;
//                     fetchVouchers(strSearch, strFromDate, strToDate);
//                   } else if (VouchersList.length > 0) {
//                     props.ViewInvoice(VouchersList[0].vouchId);
//                   }
//                 }
//               }}
//             />
//             <input
//               type="date"
//               value={strFromDate}
//               onChange={(e) => Date_Change(true, e.target.value)}
//               className="form-control"
//               autoComplete="off"
//             />
//             <input
//               type="date"
//               value={strToDate}
//               onChange={(e) => Date_Change(false, e.target.value)}
//               className="form-control"
//               autoComplete="off"
//             />
//           </div>
//           <div className="pos-view">
//             {!isLoading && VouchersList.length === 0 && (
//               <div style={{ textAlign: "center", padding: "24px", color: "#888" }}>
//                 No invoices found
//               </div>
//             )}
//             {VouchersList.map((Vouch, trIndex) => (
//               // ✅ FIX: tabIndex={trIndex} + $("[tabindex=N]") selected ANY element
//               // on the page with that tabindex. Selection/focus now use refs + state.
//               <button
//                 key={Vouch.vouchId}
//                 ref={(el) => (itemRefs.current[trIndex] = el)}
//                 className={iVouchId === Vouch.vouchId ? "active" : ""}
//                 onClick={(e) => setiVouchId(Vouch.vouchId)}
//                 onFocus={(e) => setiVouchId(Vouch.vouchId)}
//                 onDoubleClick={(e) => props.ViewInvoice(Vouch.vouchId)}
//                 onKeyDown={(e) => {
//                   const last = VouchersList.length - 1;
//                   if (e.key === "ArrowDown") {
//                     e.preventDefault();
//                     focusItem(trIndex === last ? 0 : trIndex + 1);
//                   } else if (e.key === "ArrowUp") {
//                     e.preventDefault();
//                     focusItem(trIndex === 0 ? last : trIndex - 1);
//                   } else if (e.key === "Enter") {
//                     e.preventDefault();
//                     props.ViewInvoice(Vouch.vouchId);
//                   }
//                   // Escape is handled once by the document listener above
//                 }}
//               >
//                 <div className="bill-content">
//                   <h6>{Vouch.partyName}</h6>
//                   <div className="trans-cont">
//                     <div className="transview1">
//                       <label>
//                         <span>Inv No : {Vouch.vouchNo}</span>
//                         <span>Date : {Vouch.vouchDate}</span>
//                         <span>Qty : {Number(Vouch.tQty).toFixed(2)}</span>
//                         <span>Tax Amount : {fmtINR(Vouch.tTaxAmt)}</span>
//                         <span>Net Amount : {fmtINR(Vouch.netAmt)}</span>
//                       </label>
//                       <label></label>
//                     </div>
//                   </div>
//                 </div>
//               </button>
//             ))}
//           </div>
//         </div>
//         <div className="pos-btn-group">
//           <div className="pos-tooltip" onClick={(e) => Not_Ready(e, "Print")}>
//             <a className="icon-btn" href="##">
//               <i className="fa-solid fa-print"></i>
//             </a>
//             <span className="pos-tooltiptxt">Print</span>
//           </div>
//           <div className="pos-tooltip" onClick={(e) => Not_Ready(e, "Download")}>
//             <a className="icon-btn" href="##">
//               <i className="fa-solid fa-download"></i>
//             </a>
//             <span className="pos-tooltiptxt">Download</span>
//           </div>
//           <div
//             className="pos-tooltip"
//             onClick={(e) => {
//               e.preventDefault();
//               Return_Click();
//             }}
//           >
//             <a className="icon-btn" href="##">
//               <i className="fa-solid fa-arrow-rotate-left"></i>
//             </a>
//             <span className="pos-tooltiptxt">Return</span>
//           </div>
//           <div className="pos-tooltip" onClick={(e) => Not_Ready(e, "WhatsApp share")}>
//             <a className="icon-btn" href="##">
//               <i className="fa-brands fa-whatsapp"></i>
//             </a>
//             <span className="pos-tooltiptxt">whatsapp</span>
//           </div>
//           <div className="pos-tooltip" onClick={(e) => Not_Ready(e, "E-mail")}>
//             <a className="icon-btn" href="##">
//               <i className="fa-regular fa-envelope"></i>
//             </a>
//             <span className="pos-tooltiptxt">E-mail</span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default PosView;